import { put, takeEvery, debug, createAction } from "../../lib";
import { doLOADING_SET, doLOADING_DELETE, LOADING_TIMEOUT_MS } from "..";
import { Action } from "redux";
import { redactForLogging } from "../redact_for_logging";

const DISABLED_LOADING_STATE_LIST: readonly string[] = [
  "REQUEST_COMPLETE",
  "REQUEST_ERROR",
  "REQUEST_PROJECT_FETCH",
  "REQUEST_BATCH_FETCH",
  "REQUEST_METHOD_UPDATE",
  "REQUEST_TASK_GARMIN_DEVICE_CREATE",
  "REQUEST_PROJECT_ARCHIVE_BUILD_METADATA_FETCH",
  "REQUEST_PROJECT_ARCHIVE_DOWNLOAD_METADATA_FETCH",
  "REQUEST_PROJECT_DATA_DIGEST",
  "REQUEST_PROJECT_DATA_UPDATE_REQUEST",
  "REQUEST_PROJECT_DATA_UPDATE_METADATA_FETCH",
  "REQUEST_PROJECT_DATA_DOWNLOAD_METADATA_FETCH",
  "REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_FETCH",
  "REQUEST_PROJECT_DATA_DOWNLOAD_FLOW_METADATA_LIST_FETCH",
  "REQUEST_WORKSPACE_COLLABORATOR_LIST_FETCH",
  "REQUEST_PROJECT_REALTIME_DATA_FETCH",
  "REQUEST_IDENTITY_REFRESH_TOKEN_SUBMIT",
  "REQUEST_IDENTITY_SESSION_ACTIVITY",
  "REQUEST_IDENTITY_SESSION_REVALIDATE",
] as const;

export const HANDLE_LOADING_STATE = "HANDLE_LOADING_STATE";
export const doHANDLE_LOADING_STATE = createAction(HANDLE_LOADING_STATE);

export const handleLoadingStateActionCreators = {
  doHANDLE_LOADING_STATE,
};

interface PayloadAction extends Action<string> {
  payload?: {
    requestId?: string;
    [key: string]: unknown;
  };
  disableLoadingState?: boolean;
}

const loadingTimeoutMap = new Map<string, ReturnType<typeof setTimeout>>();

const clearLoadingTimeout = (requestId?: string) => {
  if (!requestId) {
    return;
  }

  const timeoutId = loadingTimeoutMap.get(requestId);
  if (!timeoutId) {
    return;
  }

  clearTimeout(timeoutId);
  loadingTimeoutMap.delete(requestId);
};

const toJSONString = (value: unknown) => {
  try {
    return JSON.stringify(value);
  } catch {
    return "[unserializable payload]";
  }
};

const startLoadingTimeout = (action: PayloadAction) => {
  const requestId = action.payload?.requestId;
  if (!requestId) {
    return;
  }

  clearLoadingTimeout(requestId);
  const timeoutId = setTimeout(() => {
    loadingTimeoutMap.delete(requestId);
    // This error reaches the console on every stage, so the payload is redacted.
    throw new Error(
      `Loading timeout after ${LOADING_TIMEOUT_MS}ms. actionType=${action.type} payload=${toJSONString(redactForLogging(action.payload))}`,
    );
  }, LOADING_TIMEOUT_MS);
  loadingTimeoutMap.set(requestId, timeoutId);
};

export function* handleLoadingState(action: PayloadAction) {
  debug("saga*handleLoadingState");

  if (action.disableLoadingState) {
    return;
  }

  if (!DISABLED_LOADING_STATE_LIST.includes(action.type)) {
    yield put(doLOADING_SET({ requestId: action.payload?.requestId, type: action.type }));
    startLoadingTimeout(action);
  }

  if (
    action.type === "REQUEST_COMPLETE" ||
    action.type === "REQUEST_ERROR" ||
    action.type === "REQUEST_SKIP"
  ) {
    clearLoadingTimeout(action.payload?.requestId);
    yield put(doLOADING_DELETE({ requestId: action.payload?.requestId }));
  }
}

const requestOrSubmitAction = (action: PayloadAction): boolean =>
  /^REQUEST|SUBMIT$/.test(action.type);

export function* handleLoadingStateSaga() {
  yield takeEvery(requestOrSubmitAction, handleLoadingState);
}
